<template>
    <div
        v-click-outside="closeUserDropdown"
        class="workspace_self_user"
        @keydown.esc.stop.prevent="closeUserDropdown(true)"
    >
        <!-- Self-user avatar -->
        <button
            type="button"
            class="workspace_self_user_avatar"
            :aria-controls="menuId"
            :aria-expanded="isDropdownOpen"
            :aria-label="'Open status menu. ' + onlineStatusTitle"
            @click.stop="toggleUserDropdown"
        >
            <span aria-hidden="true">
                {{ userInitials }}
            </span>

            <i
                :class="onlineStatusClass"
                :title="onlineStatusTitle"
                aria-hidden="true"
            />
        </button>

        <transition name="user-status-menu">
            <div
                v-if="isDropdownOpen"
                :id="menuId"
                class="workspace_self_user_dropdown"
                role="menu"
                @click.stop
            >
                <div class="workspace_self_user_summary">
                    <span
                        class="
                            workspace_self_user_avatar
                            workspace_self_user_avatar--menu
                        "
                        aria-hidden="true"
                    >
                        {{ userInitials }}

                        <i :class="onlineStatusClass" />
                    </span>

                    <span class="workspace_self_user_identity">
                        <strong class="workspace_self_user_name">
                            {{ userDisplayName }}
                        </strong>

                        <span
                            class="workspace_self_user_current_status"
                            aria-live="polite"
                        >
                            {{ onlineStatusTitle }}
                        </span>
                    </span>
                </div>

                <div
                    v-if="!isOnline"
                    class="workspace_self_user_offline"
                    role="menuitem"
                >
                    You are currently offline
                </div>

                <ul
                    v-else
                    class="workspace_self_user_status_list"
                    aria-label="Choose your status"
                >
                    <li
                        v-for="status in statusOptions"
                        :key="status.key"
                    >
                        <button
                            type="button"
                            class="workspace_self_user_status_button"
                            :class="{
                                'workspace_self_user_status_button--active':
                                    isCurrentStatus(status)
                            }"
                            role="menuitemradio"
                            :aria-checked="isCurrentStatus(status)"
                            :disabled="isUpdatingStatus"
                            @click="updateUserStatus(status)"
                        >
                            <span
                                class="workspace_self_user_status_dot"
                                :class="
                                    'workspace_self_user_status_dot--' +
                                    status.key
                                "
                                aria-hidden="true"
                            />

                            <span>
                                {{ status.label }}
                            </span>

                            <span
                                v-if="isCurrentStatus(status)"
                                class="workspace_self_user_status_check"
                                aria-hidden="true"
                            >
                                &#10003;
                            </span>
                        </button>
                    </li>
                </ul>
            </div>
        </transition>
    </div>
</template>

<script>
import commonSocket from '@/commonSocket/index.js';
import currentUser from '@/mixins/currentUser';

const STATUS_OPTIONS = Object.freeze([
    {
        key: 'available',
        label: 'Available',
        apiValue: 'Available'
    },
    {
        key: 'busy',
        label: 'Busy',
        apiValue: 'Busy'
    },
    {
        key: 'at_a_meeting',
        label: 'At a Meeting',
        apiValue: 'At a Meeting'
    },
    {
        key: 'be_right_back',
        label: 'Be Right Back',
        apiValue: 'Be right back'
    },
    {
        key: 'lunch_away',
        label: 'Lunch Away',
        apiValue: 'Lunch Away'
    }
]);

function firstValue(values) {
    return values.find((value) => {
        return (
            value !== undefined &&
            value !== null &&
            String(value).trim() !== ''
        );
    });
}

function normalizeStatus(value) {
    return String(value || '')
        .trim()
        .replace(/\s+/g, ' ')
        .toLowerCase();
}

function toBoolean(value) {
    if (typeof value === 'boolean') {
        return value;
    }

    if (typeof value === 'number') {
        return value === 1;
    }

    if (typeof value === 'string') {
        return [
            '1',
            'true',
            'yes',
            'online'
        ].indexOf(value.trim().toLowerCase()) !== -1;
    }

    return false;
}

export default {
    name: 'SelfUserStatus',

    mixins: [
        currentUser
    ],

    data() {
        return {
            isDropdownOpen: false,
            isUpdatingStatus: false,
            statusOptions: STATUS_OPTIONS
        };
    },

    computed: {
        safeCurrentUser() {
            return this.currentUser || {};
        },

        safeSelfUserStatus() {
            return this.selfUserStatus || {};
        },

        menuId() {
            return 'self-user-status-menu-' + this._uid;
        },

        isOnline() {
            return toBoolean(
                firstValue([
                    this.safeSelfUserStatus.is_online,
                    this.safeCurrentUser.is_online,
                    this.safeCurrentUser.online
                ])
            );
        },

        userInitials() {
            const initials = [
                this.safeCurrentUser.first_name,
                this.safeCurrentUser.last_name
            ]
                .filter(Boolean)
                .slice(0, 2)
                .map((name) => {
                    return String(name)
                        .trim()
                        .charAt(0)
                        .toUpperCase();
                })
                .join('');

            if (initials) {
                return initials;
            }

            const fallback = firstValue([
                this.safeCurrentUser.name,
                this.safeCurrentUser.handle,
                this.safeCurrentUser.email
            ]);

            return fallback
                ? String(fallback).trim().charAt(0).toUpperCase()
                : '?';
        },

        userDisplayName() {
            const fullName = [
                this.safeCurrentUser.first_name,
                this.safeCurrentUser.last_name
            ]
                .filter(Boolean)
                .join(' ')
                .trim();

            return String(
                fullName ||
                firstValue([
                    this.safeCurrentUser.name,
                    this.safeCurrentUser.handle,
                    this.safeCurrentUser.email
                ]) ||
                'My profile'
            );
        },

        rawStatus() {
            return firstValue([
                this.safeSelfUserStatus.current_status,
                this.safeCurrentUser.current_status
            ]) || '';
        },

        normalizedStatus() {
            return normalizeStatus(this.rawStatus);
        },

        currentStatusOption() {
            return this.statusOptions.find((status) => {
                return (
                    normalizeStatus(status.apiValue) ===
                    this.normalizedStatus
                );
            }) || null;
        },

        formattedStatus() {
            if (!this.isOnline) {
                return 'Offline';
            }

            if (this.currentStatusOption) {
                return this.currentStatusOption.label;
            }

            return this.rawStatus
                ? String(this.rawStatus)
                : 'Available';
        },

        statusKey() {
            if (!this.isOnline) {
                return 'offline';
            }

            return this.currentStatusOption
                ? this.currentStatusOption.key
                : 'online';
        },

        onlineStatusClass() {
            return [
                'online-status',
                'online-status--' + this.statusKey
            ];
        },

        onlineStatusTitle() {
            return this.formattedStatus;
        }
    },

    methods: {
        toggleUserDropdown() {
            this.isDropdownOpen = !this.isDropdownOpen;
        },

        closeUserDropdown(returnFocus) {
            if (!this.isDropdownOpen) {
                return;
            }

            this.isDropdownOpen = false;

            if (returnFocus === true) {
                this.$nextTick(() => {
                    const button = this.$refs.menuButton;

                    if (
                        button &&
                        typeof button.focus === 'function'
                    ) {
                        button.focus();
                    }
                });
            }
        },

        isCurrentStatus(status) {
            return Boolean(
                status &&
                normalizeStatus(status.apiValue) ===
                    this.normalizedStatus
            );
        },

        async updateUserStatus(status) {
            if (
                !status ||
                !status.apiValue ||
                this.isUpdatingStatus
            ) {
                return;
            }

            this.closeUserDropdown();
            this.isUpdatingStatus = true;

            const statusTime = new Date().toISOString();
            const payload = {
                current_status: status.apiValue,
                status_time: statusTime
            };

            try {
                commonSocket.emit({
                    type: 'self_user_stat',
                    current_status: status.apiValue,
                    status_time: statusTime
                });

                const formData = new FormData();

                formData.append(
                    'user',
                    JSON.stringify(payload)
                );

                await this.$store.dispatch(
                    'updateProfileFun',
                    formData
                );

                this.$emit('status-updated', payload);
            } catch (error) {
                this.$emit('status-error', error);
            } finally {
                this.isUpdatingStatus = false;
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.workspace_self_user {
    position: relative;
    display: inline-flex;
    align-items: center;
    font-family:
        "MyriadProRegular",
        "Lato",
        sans-serif;
}

.workspace_self_user_avatar {
    padding: 0;
    outline: none;
    cursor: pointer;
    appearance: none;
}

.workspace_self_user_avatar {
    position: relative;
    display: inline-flex;
    width: 35px;
    height: 35px;
    align-items: center;
    justify-content: center;
    border:1px solid rgba(255, 255, 255, 0.08);
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.09);
    color: #d9d9d9;
    font-family:
        "MyriadProRegular",
        "Lato",
        sans-serif;
    font-size: 16px;
    font-weight: 500;
    line-height: 1;
    letter-spacing: 0.5px;
    box-shadow:
        inset 0 0 0 1px rgba(0, 0, 0, 0.12),
        0 2px 7px rgba(0, 0, 0, 0.28);
    transition:
        border-color 150ms ease,
        box-shadow 150ms ease,
        transform 150ms ease;

    &:hover {
        border-color: rgba(255, 212, 0, 0.8);
        transform: translateY(-1px);
        color: #ffd400;
    }

    &:focus {
        border-color: #ffd400;
        color: #ffd400;
        box-shadow:
            0 0 0 3px rgba(255, 212, 0, 0.2),
            0 2px 7px rgba(0, 0, 0, 0.28);
    }

    &--menu {
        width: 36px;
        height: 36px;
        border: 0;
        cursor: default;
        flex: 0 0 36px;
        box-shadow: none;

        &:hover {
            transform: none;
        }
    }
}

.online-status {
    position: absolute;
    right: -1px;
    bottom: 1px;
    width: 11px;
    height: 11px;
    border: 2px solid #171717;
    border-radius: 50%;
    background: #9e9e9e;

    &--available,
    &--online {
      background-color: #38b124;
      box-shadow: 0 0 0 2px rgba(34, 197, 94, 0.16);
    }

    &--be_right_back {
      background-color: #f3d32d;
      box-shadow: 0 0 0 2px rgba(243, 211, 45, 0.16);
    }

    &--lunch_away {
        background-color: #1483E5;
        box-shadow: 0 0 0 2px rgba(20, 131, 229, 0.16);
    }

    &--busy {
        background-color: #ee0809;
        box-shadow: 0 0 0 2px rgba(238, 8, 9, 0.16);
    }

    &--at_a_meeting {
      background-color: #f66c11;
      box-shadow: 0 0 0 2px rgba(246, 108, 17, 0.16);
    }

    &--offline {
        background: #9e9e9e;
    }
}

.workspace_self_user_dropdown {
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    width: 250px;
    max-width: calc(100vw - 24px);
    padding: 10px;
    border:
        1px solid rgba(255, 255, 255, 0.16);
    border-radius: 10px;
    background: #171717;
    color: #f5f5f5;
    box-shadow:
        0 14px 34px rgba(0, 0, 0, 0.5),
        0 3px 8px rgba(0, 0, 0, 0.35);
    transform-origin: top right;

    &::before {
        position: absolute;
        top: -6px;
        right: 18px;
        width: 11px;
        height: 11px;
        border-top:
            1px solid rgba(255, 255, 255, 0.16);
        border-left:
            1px solid rgba(255, 255, 255, 0.16);
        background: #171717;
        content: "";
        transform: rotate(45deg);
    }
}

.workspace_self_user_summary {
    display: flex;
    padding: 4px 4px 11px;
    align-items: center;
    border-bottom:
        1px solid rgba(255, 255, 255, 0.05);
}

.workspace_self_user_identity {
    display: block;
    min-width: 0;
    padding-left: 11px;
}

.workspace_self_user_name,
.workspace_self_user_current_status {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.workspace_self_user_name {
    color: #ffffff;
    font-size: 15px;
    font-weight: 500;
    letter-spacing: 0.2px;
    line-height: 1.25;
}

.workspace_self_user_current_status {
    margin-top: 3px;
    color: rgba(255, 255, 255, 0.58);
    font-size: 12px;
    line-height: 1.2;
}

.workspace_self_user_status_list {
    max-height: 230px;
    padding: 7px 0 0;
    margin: 0;
    overflow-x: hidden;
    overflow-y: auto;
    list-style: none;

    li {
        padding: 0;
        margin: 0;
    }

    li + li {
        margin-top: 2px;
    }
}

.workspace_self_user_status_button {
    display: flex;
    width: 100%;
    min-height: 36px;
    padding: 8px 10px;
    align-items: center;
    border: 0;
    border-radius: 7px;
    outline: none;
    background: transparent;
    color: rgba(255, 255, 255, 0.82);
    font-family: inherit;
    font-size: 14px;
    line-height: 1.2;
    text-align: left;
    cursor: pointer;
    transition:
        color 130ms ease,
        background-color 130ms ease;

    &:hover,
    &:focus {
        background: rgba(255, 255, 255, 0.08);
        color: #ffffff;
    }

    &--active {
        background: rgba(255, 212, 0, 0.11);
        color: #ffd400;
    }

    &:disabled {
        cursor: wait;
        opacity: 0.58;
    }
}

.workspace_self_user_status_dot {
    width: 10px;
    height: 10px;
    margin-right: 10px;
    border-radius: 50%;
    background: #9e9e9e;
    flex: 0 0 10px;

    &--available {
        background: #38b124;
    }

    &--busy {
        background: #ee0809;
    }

    &--at_a_meeting {
        background: #f66c11;
    }

    &--be_right_back {
        background: #f3d32d;
    }

    &--lunch_away {
        background: #1483e5;
    }
}

.workspace_self_user_status_check {
    margin-left: auto;
    padding-left: 10px;
    color: #ffd400;
    font-size: 15px;
    font-weight: 700;
}

.workspace_self_user_offline {
    padding: 12px 9px 4px;
    color: rgba(255, 255, 255, 0.58);
    font-size: 13px;
    line-height: 1.35;
    text-align: center;
}

.user-status-menu-enter-active,
.user-status-menu-leave-active {
    transition:
        opacity 130ms ease,
        transform 130ms cubic-bezier(0.4, 0, 0.2, 1);
}

.user-status-menu-enter,
.user-status-menu-leave-to {
    opacity: 0;
    transform:
        translate3d(0, -6px, 0)
        scale(0.98);
}

@media only screen and (max-width: 767px) {
    .workspace_self_user_dropdown {
        right: -4px;
        width: 238px;
    }
}

@media (prefers-reduced-motion: reduce) {
    .workspace_self_user_avatar,
    .workspace_self_user_status_button,
    .user-status-menu-enter-active,
    .user-status-menu-leave-active {
        transition: none;
    }
}
</style>
